"use client";

import { useMemo } from "react";
import { LuRefreshCw, LuSettings2 } from "react-icons/lu";

import { StickyStack } from "@/components/shared/sticky-stack";
import { SurfaceSelectorRow } from "@/components/shared/surface-selector-row";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  areFeedSurfaceRefsEqual,
  type FeedSurfaceRef,
  getFeedSurfaceIcon,
} from "@/utils/feed-surface";
import type { FocusSurfaceResolution } from "@/utils/focus-preferences";
import { cn } from "@/utils/lib";

type FeedSelectorRowProps = {
  activeSurface: FeedSurfaceRef;
  surfaces: FocusSurfaceResolution[];
  onSelectSurface: (surface: FeedSurfaceRef) => void;
  onRefresh: () => void;
  onOpenSettings: () => void;
  refreshing?: boolean;
  showSettings?: boolean;
};

export const FeedSelectorRow = ({
  activeSurface,
  surfaces,
  onSelectSurface,
  onRefresh,
  onOpenSettings,
  refreshing = false,
  showSettings = true,
}: FeedSelectorRowProps) => {
  const activeItemKey =
    surfaces.find((surface) =>
      areFeedSurfaceRefsEqual(surface.ref, activeSurface),
    )?.key ?? "";
  const items = useMemo(
    () =>
      surfaces.map((surface) => ({
        icon: getFeedSurfaceIcon(surface.iconKey),
        key: surface.key,
        label: surface.label,
        value: surface.ref,
      })),
    [surfaces],
  );

  return (
    <SurfaceSelectorRow
      activeItemKey={activeItemKey}
      items={items}
      onSelectItem={(surface) => {
        onSelectSurface(surface);
      }}
      actions={
        <>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9 rounded-full"
            onClick={onRefresh}
          >
            <LuRefreshCw
              className={cn("size-4", refreshing && "animate-spin")}
            />
            <span className="sr-only">Refresh feed</span>
          </Button>

          {showSettings && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-9 rounded-full"
              onClick={onOpenSettings}
            >
              <LuSettings2 className="size-4" />
              <span className="sr-only">Open feed manager</span>
            </Button>
          )}
        </>
      }
    />
  );
};

export const FeedSelectorRowSkeleton = ({
  showSettings = true,
}: {
  showSettings?: boolean;
}) => {
  return (
    <StickyStack
      className="
        unforce-radius z-20 flex h-16 items-center gap-2 border-y border-border
        bg-background/95 px-2 backdrop-blur-md
      "
    >
      <div className="min-w-0 flex-1">
        <div className="flex w-full items-center gap-1 overflow-hidden pr-2">
          <Skeleton className="h-9 w-[92px] shrink-0 rounded-full" />
          <Skeleton className="h-9 w-[104px] shrink-0 rounded-full" />
          <Skeleton
            className="
              h-9 w-[42px] shrink-0 rounded-full
              md:w-[136px]
            "
          />
          <Skeleton
            className="
              hidden h-9 w-[84px] shrink-0 rounded-full
              md:block
            "
          />
          <Skeleton
            className="
              hidden h-9 w-[126px] shrink-0 rounded-full
              md:block
            "
          />
          <Skeleton
            className="
              hidden h-9 w-[42px] shrink-0 rounded-full
              md:block
            "
          />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Skeleton className="size-9 rounded-full" />
        {showSettings && <Skeleton className="size-9 rounded-full" />}
      </div>
    </StickyStack>
  );
};
